/**
 * Phase 2 (Feb 2026, updated) — ProfiChat with real /profi-chat/message
 * wiring, Class-C action-cards, injection-detection badges + approval
 * flow.
 *
 * Contract with backend (see app/domains/profi_chat/__init__.py):
 *   POST /api/v1/profi-chat/message { content, locale } →
 *     { message_id, role, content, untrusted, untrusted_reasons,
 *       action_card | null, trace }
 *   POST /api/v1/profi-chat/approvals/{approval_id}/approve →
 *     { approval_id, state, decided_by, decided_at }
 *
 * The frontend NEVER executes the underlying action. Approval flips
 * the card to "approved" and shows a distinct manual-execute link
 * pointing at the appropriate handbrake / policy-guard action.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Bot, Sparkles, Send, ArrowRight, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

// ── ActionCard (Class-C) ────────────────────────────────────────────

function ActionCard({ card, onApprove }) {
  const bindingPrefix = card?.param_binding_hash_prefix || "";
  const approved = card.state === "approved";
  return (
    <div
      data-testid="profi-chat-action-card"
      data-state={card.state}
      className={
        "mt-3 rounded-md border p-4 space-y-3 " +
        (approved
          ? "border-emerald-500/60 bg-emerald-50/40 dark:bg-emerald-950/20"
          : "border-amber-300/60 bg-amber-50/40 dark:bg-amber-950/20")
      }
    >
      <div className="flex items-center gap-2">
        <Badge variant="secondary" data-testid="action-card-class">
          Klasse {card.execution_class || "C"}
        </Badge>
        <span className="text-sm font-medium">{card.title}</span>
        {approved && (
          <Badge
            variant="default"
            className="ml-auto"
            data-testid="action-card-approved-badge"
          >
            <CheckCircle2 className="w-3 h-3 mr-1" />
            genehmigt
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
        <MetaRow label="Objekt-Version" value={card.object_version} testid="ac-obj-version" />
        <MetaRow label="Bindungs-Hash" value={bindingPrefix + "…"} testid="ac-binding-hash" />
        <MetaRow label="Aktor" value={card.actor} testid="ac-actor" />
        <MetaRow
          label="Geschätzte Kosten"
          value={card.cost_estimate_eur != null ? `${card.cost_estimate_eur} €` : "—"}
          testid="ac-cost"
        />
        <MetaRow
          label="Gültig bis"
          value={card.valid_until ? new Date(card.valid_until).toLocaleString() : "—"}
          testid="ac-valid-until"
        />
        <MetaRow
          label="Betroffene Objekte"
          value={(card.affected_objects || []).join(", ") || "—"}
          testid="ac-affected"
        />
      </div>

      {card.params_preview && (
        <div className="text-xs space-y-1" data-testid="ac-params-preview">
          <div className="text-muted-foreground">Vorher / Nachher:</div>
          <pre className="font-mono text-[11px] whitespace-pre-wrap bg-background/70 border rounded px-2 py-1">
{JSON.stringify(card.params_preview, null, 2)}
          </pre>
        </div>
      )}

      {approved && (
        <div
          className="text-[11px] text-emerald-700 dark:text-emerald-400"
          data-testid="ac-approved-meta"
        >
          Genehmigt von {card.decided_by} · {new Date(card.decided_at).toLocaleString()}
          <div className="pt-1 text-muted-foreground">
            Ausführung passiert NICHT automatisch. Löse die Aktion bewusst über
            die passende Handbremse / den Kampagnen-Wizard aus.
          </div>
        </div>
      )}

      <div className="flex gap-2 pt-1 flex-wrap">
        {!approved && (
          <Button
            size="sm"
            variant="default"
            data-testid="action-card-request-approval"
            onClick={() => onApprove(card)}
          >
            Genehmigen
          </Button>
        )}
        <Button size="sm" variant="ghost" asChild>
          <Link to="/app/approvals" data-testid="action-card-open-approvals">
            Alle Freigaben
            <ArrowRight className="w-3 h-3 ml-1" />
          </Link>
        </Button>
      </div>
    </div>
  );
}

function MetaRow({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between rounded border bg-background/70 px-2 py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono text-[11px]" data-testid={testid}>
        {value ?? "—"}
      </span>
    </div>
  );
}

// ── Phase 6c — BriefPreview + AgentRunBadge ─────────────────────────

function BriefPreview({ data, briefingId, run, idx }) {
  const _pt = Array.isArray(data.primary_texts) ? data.primary_texts : [];
  return (
    <div
      data-testid={`profi-chat-brief-preview-${idx}`}
      className="mt-3 rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2 text-xs"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="default" data-testid="brief-preview-title">
          {data.title || "Kampagnen-Entwurf"}
        </Badge>
        {data.channels && data.channels.length > 0 && (
          <span
            className="text-muted-foreground"
            data-testid="brief-preview-channels"
          >
            Kanäle: {data.channels.slice(0, 4).join(", ")}
          </span>
        )}
        {data.daily_budget_eur != null && (
          <span
            className="ml-auto text-muted-foreground"
            data-testid="brief-preview-budget"
          >
            Tagesbudget: {data.daily_budget_eur} €
          </span>
        )}
      </div>
      {data.target_audience && (
        <div data-testid="brief-preview-audience">
          <span className="text-muted-foreground">Zielgruppe:</span>{" "}
          {typeof data.target_audience === "string"
            ? data.target_audience
            : JSON.stringify(data.target_audience).slice(0, 200)}
        </div>
      )}
      {_pt.length > 0 && (
        <div className="space-y-1" data-testid="brief-preview-ads">
          {_pt.map((t, k) => (
            <div key={k} className="pl-2 border-l-2 border-primary/40">
              {typeof t === "string" ? t : (t.text || JSON.stringify(t).slice(0, 200))}
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2 pt-1 border-t border-muted">
        {run && (
          <span
            className="text-[10px] text-muted-foreground"
            data-testid="brief-preview-run-meta"
          >
            {run.model || "agent"} · {run.latency_ms ? `${run.latency_ms}ms` : "—"}
            {run.cost_usd != null ? ` · $${Number(run.cost_usd).toFixed(4)}` : ""}
          </span>
        )}
        {briefingId && (
          <Link
            to={`/app/campaigns/wizard?briefing=${briefingId}`}
            className="ml-auto underline text-primary"
            data-testid="brief-preview-open-wizard"
          >
            Feintuning im Wizard
          </Link>
        )}
      </div>
    </div>
  );
}

function AgentRunBadge({ run, idx }) {
  if (run.error) {
    return (
      <div
        data-testid={`profi-chat-agent-error-${idx}`}
        className="mt-2 text-[11px] font-mono text-red-700 dark:text-red-400"
      >
        Agent-Fehler: {run.error}
      </div>
    );
  }
  return (
    <div
      data-testid={`profi-chat-agent-run-${idx}`}
      className="mt-2 text-[10px] text-muted-foreground"
    >
      {run.model || "agent"} · {run.status || "…"}
      {run.latency_ms ? ` · ${run.latency_ms}ms` : ""}
    </div>
  );
}


// Phase 6c-b — surfaces the onboarding-missing follow-up as a
// compact panel with a deep-link that returns the user to the wizard.
function FollowUpPanel({ follow_up, idx }) {
  const missing = (follow_up.missing_fields || []).filter(Boolean);
  const _labels = {
    regions: "Regionen",
    target_groups: "Zielgruppen",
    products_or_services: "Produkte oder Leistungen",
  };
  return (
    <div
      data-testid={`profi-chat-follow-up-${idx}`}
      className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 space-y-2 text-xs"
    >
      <div
        className="font-medium text-amber-900 dark:text-amber-200"
        data-testid="profi-chat-follow-up-kind"
      >
        Onboarding fehlt für einen zuverlässigen Vorschlag
      </div>
      {missing.length > 0 && (
        <ul
          className="list-disc list-inside text-amber-800 dark:text-amber-100"
          data-testid="profi-chat-follow-up-missing"
        >
          {missing.map((f) => (
            <li key={f}>{_labels[f] || f}</li>
          ))}
        </ul>
      )}
      <Link
        to={follow_up.wizard_href || "/app/onboarding"}
        className="inline-block underline text-primary"
        data-testid="profi-chat-follow-up-open-wizard"
      >
        Zum Einrichtungs-Wizard
      </Link>
    </div>
  );
}


// ── Main widget ─────────────────────────────────────────────────────

export default function ProfiChat({ variant = "hero" }) {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi. Ich bin dein Profi-Assistent. Sag mir kurz, was du erreichen willst " +
        "— z. B. mehr Anfragen fuer 3-Zimmer-Wohnungen in Wuppertal. Sobald eine " +
        "Klasse-C-Aktion (z. B. Budget-Erhoehung, Veroeffentlichen, Werbestopp) " +
        "noetig ist, blende ich unten eine Freigabe-Karte ein.",
      untrusted: false,
    },
  ]);

  async function send() {
    if (!prompt.trim() || busy) return;
    setBusy(true);
    const userMsg = { role: "user", content: prompt.trim(), untrusted: false };
    setMessages((m) => [...m, userMsg]);
    setPrompt("");
    try {
      const r = await api.post("/profi-chat/message", {
        content: userMsg.content,
        locale: "de",
      });
      const d = r.data || {};
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: d.content || "",
          untrusted: !!d.untrusted,
          untrusted_reasons: d.untrusted_reasons || [],
          card: d.action_card || null,
          // Phase 6c — agent-delegation surfaces:
          agent_run: d.agent_run || null,
          brief_preview: d.brief_preview || null,
          briefing_id: d.briefing_id || null,
          // Phase 6c-b — chat asked because onboarding is empty
          follow_up: d.follow_up || null,
        },
      ]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" ? detail.error : detail;
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            code === "forbidden_top_level_key" ||
            code === "prompt_injection_detected"
              ? "Der Vorschlag wurde vom Sicherheitswächter zurückgewiesen. Bitte formuliere die Anfrage neu."
              : "Da ist etwas schiefgelaufen. Bitte erneut versuchen.",
          untrusted: false,
          error: true,
        },
      ]);
      toast.error(code || "Chat-Fehler");
    } finally {
      setBusy(false);
    }
  }

  async function approveCard(card) {
    try {
      const r = await api.post(
        `/profi-chat/approvals/${card.approval_id}/approve`,
      );
      const decidedBy = r.data?.decided_by;
      const decidedAt = r.data?.decided_at;
      setMessages((prev) =>
        prev.map((m) =>
          m.card && m.card.approval_id === card.approval_id
            ? {
                ...m,
                card: {
                  ...m.card,
                  state: "approved",
                  decided_by: decidedBy,
                  decided_at: decidedAt,
                },
              }
            : m,
        ),
      );
      toast.success("Freigabe erteilt · Ausführung braucht separaten Trigger");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail?.error || "Freigabe fehlgeschlagen",
      );
    }
  }

  return (
    <Card
      data-testid="profi-chat"
      data-variant={variant}
      className={
        variant === "hero"
          ? "border-primary/30 shadow-lg"
          : "border-muted"
      }
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Bot className="w-4 h-4 text-primary" />
          Profi-Chat
          <Badge variant="outline" className="ml-2" data-testid="profi-chat-single-source-badge">
            <Sparkles className="w-3 h-3 mr-1" />
            Ein Briefing für Chat &amp; Assistent
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div
          className="max-h-[420px] overflow-y-auto space-y-3 pr-1"
          data-testid="profi-chat-messages"
        >
          {messages.map((m, i) => (
            <div
              key={i}
              className={
                "rounded-md px-3 py-2 text-sm " +
                (m.role === "user"
                  ? "bg-primary/10 border border-primary/20"
                  : m.error
                  ? "bg-destructive/10 border border-destructive/30"
                  : m.untrusted
                  ? "bg-red-50 dark:bg-red-950/30 border-2 border-red-500"
                  : "bg-muted/40 border border-muted")
              }
              data-testid={`profi-chat-msg-${i}`}
              data-untrusted={m.untrusted ? "true" : "false"}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {m.role === "user" ? "Du" : "VIONA"}
                </span>
                {m.untrusted && (
                  <Badge
                    variant="destructive"
                    className="text-[10px]"
                    data-testid={`profi-chat-untrusted-${i}`}
                  >
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Nicht vertrauenswürdiger Inhalt — kann keine Aktionen autorisieren
                  </Badge>
                )}
              </div>
              <div className="whitespace-pre-wrap">{m.content}</div>
              {m.untrusted && (m.untrusted_reasons || []).length > 0 && (
                <div
                  className="text-[10px] text-red-600 mt-1 font-mono"
                  data-testid={`profi-chat-untrusted-reasons-${i}`}
                >
                  {(m.untrusted_reasons || []).join(", ")}
                </div>
              )}
              {m.card && <ActionCard card={m.card} onApprove={approveCard} />}
              {m.brief_preview && (
                <BriefPreview
                  data={m.brief_preview}
                  briefingId={m.briefing_id}
                  run={m.agent_run}
                  idx={i}
                />
              )}
              {!m.brief_preview && m.agent_run && (
                <AgentRunBadge run={m.agent_run} idx={i} />
              )}
              {m.follow_up && (
                <FollowUpPanel follow_up={m.follow_up} idx={i} />
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2 items-end">
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={2}
            placeholder="Was möchtest du erreichen?"
            data-testid="profi-chat-input"
            className="resize-none"
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
            }}
          />
          <Button
            onClick={send}
            disabled={busy || !prompt.trim()}
            data-testid="profi-chat-send"
          >
            <Send className="w-4 h-4 mr-1" />
            Senden
          </Button>
        </div>
        <div className="text-[11px] text-muted-foreground flex items-center justify-between">
          <span>Cmd/Strg+Enter zum Senden</span>
          <span className="flex items-center gap-3">
            <Link
              to="/app/campaigns/wizard"
              className="underline hover:text-foreground"
              data-testid="profi-chat-open-wizard"
            >
              Zum Wizard
            </Link>
            <Link
              to="/app/assistant"
              className="underline hover:text-foreground"
              data-testid="profi-chat-open-full"
            >
              Vollansicht öffnen
            </Link>
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
